/**
 * The Billing tab: who is billed, on one line. An estimate takes no payment —
 * the catalogue estimate link's payments retired with the link (order to
 * invoice step 3b); an order's payments are on the order page.
 */
import type { EstimateDetailViewItem } from "@shared-core/data/api-types";
import { addressLines } from "@shared-core/domain/estimate-presentation";
import { InfoTip } from "../../app/components/patterns/InfoTip";
import { Gap, TabContainer } from "./atoms";

export function BillingTab({ estimate }: { estimate: EstimateDetailViewItem }) {
  return (
    <TabContainer label="Billing details">
      <BillTo billing={estimate.delivery_billing.billing} />
    </TabContainer>
  );
}

/**
 * Who is billed, on one line: the account and its ref, the invoice contacts
 * (their details in the ⓘ), and the billing address — with its contact and
 * comment — or why there is none. Billing details are held against the
 * customer, not this estimate.
 */
function BillTo({
  billing,
}: {
  billing: EstimateDetailViewItem["delivery_billing"]["billing"];
}) {
  const contacts = billing.invoice_contacts ?? [];
  const account =
    billing.posting_account_name && billing.posting_account
      ? `${billing.posting_account_name} (${billing.posting_account})`
      : (billing.posting_account_name ?? billing.posting_account);
  const heldAddress = billing.billing_address;
  const address = heldAddress ? addressLines(heldAddress).join(", ") : null;
  return (
    <section aria-label="Billing" className="py-4">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <span className="text-muted-foreground">Bill to</span>
        {account ? (
          <span className="font-medium">{account}</span>
        ) : (
          <Gap>
            {billing.coverage === "unavailable"
              ? "No customer billing record could be read for this estimate."
              : "No account recorded"}
          </Gap>
        )}
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        {contacts.length === 0 ? (
          <Gap>No invoice contact is flagged on this customer</Gap>
        ) : (
          <span className="inline-flex items-center gap-1">
            {contacts
              .map((c) => c.name ?? `Contact ${c.contact_id}`)
              .join(", ")}
            <InfoTip label="Invoice contacts">
              {contacts
                .map((c) =>
                  [
                    c.name ?? `Contact ${c.contact_id}`,
                    c.job_title,
                    c.email,
                    c.phone,
                  ]
                    .filter(Boolean)
                    .join(" — "),
                )
                .join("; ")}
            </InfoTip>
          </span>
        )}
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        {heldAddress ? (
          <>
            {address ? (
              <span>{address}</span>
            ) : (
              <Gap>Address held but empty</Gap>
            )}
            {heldAddress.contact && (
              <span className="text-muted-foreground">
                Contact: {heldAddress.contact}
              </span>
            )}
            {heldAddress.comment && (
              <span className="text-muted-foreground">
                {heldAddress.comment}
              </span>
            )}
          </>
        ) : (
          // Deliberately not the delivery address: a delivery address in a
          // billing slot is a wrong fact, not a partial one.
          <span className="inline-flex items-center gap-1">
            <Gap>Billing address not held</Gap>
            <InfoTip label="Why there is no billing address">
              No billing address exists in the Tharstern replica, and the
              delivery address is not a substitute for one.
            </InfoTip>
          </span>
        )}
        <InfoTip label="About billing">
          Billing details are held against the customer, not this estimate.
        </InfoTip>
      </p>
    </section>
  );
}
